import AirIcon from '@mui/icons-material/Air';
import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { WindDataItemFormattedWithUnits, useHistoricalWindData } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import {
    ChartDataDetails,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineDirectionMean,
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { WindDatumDetail, WindLineConfig } from '@/components/spotter/historicalDataTab/datumDetail/WindDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { composeAlertLines } from '@/utils';

export const WIND_SPEED_LINE = {
    id: 'wind-speed',
    label: 'Wind speed',
    valueKey: 'speed' as const,
    color: colorsets.blue,
    unitKey: 'windSpeedUnits' as const,
    unitTypeKey: 'windSpeedUnitType' as const,
} satisfies Line<WindDataItemFormattedWithUnits>;

type LineWithDirectionConfig = Line<WindDataItemFormattedWithUnits> & WindLineConfig;

const LINES = [{ ...WIND_SPEED_LINE, directionKey: 'direction' as const }] satisfies LineWithDirectionConfig[];

export type WindSpeedChartProps<L extends Line<WindDataItemFormattedWithUnits>> = {
    chartTitle: string;
    spotterId: string;
    queryEnabled?: boolean;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: L[];
    onSelectData?: (data: LineDataSlice<WindDataItemFormattedWithUnits, L> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const WindSpeedChart = <L extends Line<WindDataItemFormattedWithUnits>>({
    chartTitle,
    spotterId,
    queryEnabled = true,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    onSelectData,
    onSelectTimestamp,
    lines,
}: WindSpeedChartProps<L>) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<WindDataItemFormattedWithUnits, L> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `wind-speed-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    const { data: historicalWind, isPending } = useHistoricalWindData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
        config: { enabled: queryEnabled },
    });

    const drawChart = useCallback(drawLineChart, []);

    const linesWithRenderers = useMemo(
        () => addRenderersByProcessingSource(lines, historicalWind),
        [historicalWind, lines]
    );

    useEffect(() => {
        if (isPending) {
            return;
        }
        if (!historicalWind) {
            return;
        }

        const newChart = drawChart<WindDataItemFormattedWithUnits, L>({
            dataset: historicalWind,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: `Wind speed`,
            containerId,
            showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        historicalWind,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        showAlertLines,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [historicalWind?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const WindSpeedAndDirectionSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const chartTitle = 'Wind speed and direction';
    const { spotterId } = useSpotterContext();
    const { dateRange } = useDateRangeContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<
        WindDataItemFormattedWithUnits,
        LineWithDirectionConfig
    > | null>(null);

    const {
        data: historicalWind,
        isPending,
        isFetching,
    } = useHistoricalWindData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
    });

    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });
    const alertLines = useMemo(
        () => composeAlertLines({ deviceAlerts, triggerTypes: ['spotter_threshold_windspeed'] }),
        [deviceAlerts]
    );

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon Icon={AirIcon} />}
        >
            {historicalWind?.length ? (
                <Box>
                    <ChartWithInfo
                        legend={<ProcessingSourceLegendItems data={historicalWind} />}
                        chart={
                            <WindSpeedChart
                                onSelectTimestamp={setSelectedTimestamp}
                                lines={LINES}
                                spotterId={spotterId!}
                                chartTitle={chartTitle}
                                onSelectData={setSelectedData}
                                showAlertLines={isAlertLinesOn}
                                scaleToFitAlerts={isScaleToFitAlertLinesOn}
                                alertLines={alertLines}
                            />
                        }
                    >
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={LINES}
                                    dataset={historicalWind}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                            <DataLineDirectionMean
                                                label="Averaged wind direction"
                                                unit="deg"
                                                type="source"
                                                valueKey="direction"
                                            />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) =>
                                    lines.map((line) => (
                                        <WindDatumDetail
                                            key={line.id}
                                            dataset={historicalWind}
                                            datum={datum}
                                            line={line}
                                        />
                                    ))
                                }
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Box>
            ) : (
                <Alert severity="info">No wind data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
